// AstriaGCCV2Shell.tsx
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Clock, Wind, MessageCircle, Heart, Fingerprint } from "lucide-react";
import {
  useGCCV2Language,
  GCCV2Language,
} from "../../../utils/GCC/astriaGCCV2Language";
import { L, LC, useLnLanguage } from "../../../utils/lnText";

// ─── Astria GCC v2 — shared shell (header + tab bar)
export const GCC2_HEADING_FONT_EN = "'Playfair Display', serif";
export const GCC2_BODY_FONT_EN = "'Inter', sans-serif";
export const GCC2_HEADING_FONT_AR = "'Noto Naskh Arabic', serif";
export const GCC2_BODY_FONT_AR = "'Noto Sans Arabic', sans-serif";

export const getGCCV2Fonts = (language: GCCV2Language) => ({
  heading: language === "ar" ? GCC2_HEADING_FONT_AR : GCC2_HEADING_FONT_EN,
  body: language === "ar" ? GCC2_BODY_FONT_AR : GCC2_BODY_FONT_EN,
});

export const SHELL_COLORS_GCC_V2 = {
  bg: "linear-gradient(160deg, #FFFDF8 0%, #FBF6EC 100%)",
  card: "#FFFFFF",
  section: "#FBF6EC",
  cardBorder: "#EAE0C8",
  titleColor: "#2B2725",
  sectionTitleColor: "#2B2725",
  bodyColor: "#3F3A35",
  mutedColor: "#8A8178",
  accent: "#C9A24B",
  accentSoft: "rgba(201,162,75,0.12)",
  accentGlow: "0 0 0 3px rgba(201,162,75,0.18)",
  white: "#FFFFFF",
};

export type AstriaGCCV2Tab =
  | "timing"
  | "kyusei"
  | "companion"
  | "emotional"
  | "personality";

const TABS: {
  key: AstriaGCCV2Tab;
  label: { en: string; ar: string };
  icon: React.ReactNode;
  path: string;
}[] = [
  {
    key: "timing",
    label: { en: "Timing", ar: "الإيقاع" },
    icon: <Clock className="w-4 h-4" />,
    path: "/astria-gcc-v2/timing",
  },
  {
    key: "kyusei",
    label: { en: "Kyusei", ar: "النمط" },
    icon: <Wind className="w-4 h-4" />,
    path: "/astria-gcc-v2/kyusei",
  },
  {
    key: "companion",
    label: { en: "Companion", ar: "الرفيق" },
    icon: <MessageCircle className="w-4 h-4" />,
    path: "/astria-gcc-v2/companion",
  },
  {
    key: "emotional",
    label: { en: "Emotional Intelligence", ar: "الذكاء العاطفي" },
    icon: <Heart className="w-4 h-4" />,
    path: "/astria-gcc-v2/emotional-intelligence",
  },
  {
    key: "personality",
    label: { en: "Personality", ar: "الشخصية" },
    icon: <Fingerprint className="w-4 h-4" />,
    path: "/astria-gcc-v2/personality-engine",
  },
];

interface AstriaGCCV2ShellProps {
  active: AstriaGCCV2Tab;
  children: React.ReactNode;
}

const AstriaGCCV2Shell: React.FC<AstriaGCCV2ShellProps> = ({
  active,
  children,
}) => {
  useLnLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const language: GCCV2Language = useGCCV2Language();
  const fonts = getGCCV2Fonts(language);
  const dir = language === "ar" ? "rtl" : L("ltr");

  const handleTabChange = (tabKey: AstriaGCCV2Tab) => {
    const tab = TABS.find((t) => t.key === tabKey);
    if (tab && tab.path !== location.pathname) {
      navigate(tab.path);
    }
  };

  return (
    <div
      dir={dir}
      className="min-h-screen px-4 py-10"
      style={{ background: SHELL_COLORS_GCC_V2.bg, fontFamily: fonts.body }}
    >
      <div
        className="mx-auto"
        style={{
          background: SHELL_COLORS_GCC_V2.card,
          borderRadius: 18,
          boxShadow: "0 8px 32px rgba(43,39,37,0.08)",
          maxWidth: 760,
          padding: 32,
        }}
      >
        {/* ─── Header ────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: SHELL_COLORS_GCC_V2.titleColor,
              marginBottom: 4,
              lineHeight: 1.3,
              letterSpacing: "0.01em",
              fontFamily: fonts.heading,
            }}
          >
            {language === "ar" ? "أستريا الخليج" : L("Astria GCC")}
          </div>
          <p
            style={{
              fontSize: 12,
              color: SHELL_COLORS_GCC_V2.mutedColor,
              lineHeight: 1.6,
              letterSpacing: "0.02em",
            }}
          >
            {language === "ar"
              ? "مساحة هادئة تفهمك."
              : L("A quiet space that understands you.")}
          </p>
        </div>

        {/* ─── Tab Bar ────────────────────────────────────────────────────── */}
        <div
          className="flex gap-1 overflow-x-auto"
          style={{
            marginBottom: 28,
            background: SHELL_COLORS_GCC_V2.section,
            borderRadius: 14,
            padding: 4,
          }}
        >
          {TABS.map((tab) => {
            const isActive = tab.key === active;
            const label = LC(tab.label, language);

            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.boxShadow =
                      SHELL_COLORS_GCC_V2.accentGlow;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.boxShadow = "none";
                  }
                }}
                className="flex items-center justify-center gap-2 flex-1 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-300"
                style={{
                  paddingInlineStart: 12,
                  paddingInlineEnd: 12,
                  paddingTop: 10,
                  paddingBottom: 10,
                  background: isActive
                    ? SHELL_COLORS_GCC_V2.white
                    : "transparent",
                  color: isActive
                    ? SHELL_COLORS_GCC_V2.accent
                    : SHELL_COLORS_GCC_V2.mutedColor,
                  boxShadow: isActive
                    ? "0 2px 10px rgba(201,162,75,0.15)"
                    : "none",
                  fontWeight: isActive ? 600 : 500,
                }}
              >
                <span
                  style={{
                    opacity: isActive ? 1 : 0.6,
                    transition: "opacity 0.2s",
                  }}
                >
                  {tab.icon}
                </span>
                <span>{label}</span>
              </button>
            );
          })}
        </div>

        {/* ─── Content ────────────────────────────────────────────────────── */}
        <div
          className="relative"
          style={{ color: SHELL_COLORS_GCC_V2.bodyColor }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default AstriaGCCV2Shell;
